import { LocalTime } from '@js-joda/core';
import { useWatch } from 'react-hook-form';

import { DatePicker, StyledMaxWidth, TimePicker } from '@dreryk/react-components';

import { appointmentsTranslationKeys } from '../../../../../translations';
import { AppointmentFormController } from '../AppointmentForm.types';
import { ReceptionServiceTableBean } from '../../../../../receptionService/extensions/calendar/appointment/interactiveTable/ReceptionServiceInteractiveTable.types';

import {
   useAppointmentNewSlotAvailabilityChecker,
   useSyncTimeWithEstimatedDuration,
} from './AppointmentDateTimeRangePicker.hooks';
import { syncEndTime, syncEstimatedDurationValue, syncStartTime } from './AppointmentDateTimeRangePicker.utils';

const TIME_STEP_IN_MINUTES = 5;
const MIN_TIME = LocalTime.MIN;
const MAX_TIME = LocalTime.MIDNIGHT;

type AppointmentDateTimeRangePickerProps = Pick<
   AppointmentFormController,
   'register' | 'getValue' | 'setValue' | 'trigger' | 'setError' | 'watch' | 'formState' | 'control'
> & { readonly appointmentId?: number };

// eslint-disable-next-line max-lines-per-function
export const AppointmentDateTimeRangePicker = (props: AppointmentDateTimeRangePickerProps): JSX.Element => {
   useSyncTimeWithEstimatedDuration(props.control, props.formState, props.setValue, props.watch);

   const receptionServiceValue = useWatch<ReceptionServiceTableBean[]>({
      name: 'receptionService.value',
      control: props.control,
      defaultValue: [],
   });

   const checkAppointmentNewSlotAvailability = useAppointmentNewSlotAvailabilityChecker(props.appointmentId);

   return (
      <>
         <DatePicker
            controller={props.register('date', {
               required: true,
            })}
            label={appointmentsTranslationKeys.update.form.date}
            testId={'date'}
            withNavigationArrows
         />
         <StyledMaxWidth maxWidth={150}>
            <TimePicker
               controller={props.register('startTime', {
                  required: true,
                  validate: async (data) => {
                     syncEndTime(props.setValue, props.getValue, receptionServiceValue);

                     const date = props.getValue('date');
                     const startTime = data as LocalTime;
                     const endTime = props.getValue('endTime');

                     if (date) {
                        const availableSlot = await checkAppointmentNewSlotAvailability(date, startTime, endTime);

                        if (!availableSlot) {
                           props.setError('endTime', true);
                           return appointmentsTranslationKeys.timeSlotOccupied;
                        }

                        props.setError('endTime', false);
                     }

                     return undefined;
                  },
               })}
               label={appointmentsTranslationKeys.update.form.startTime}
               min={MIN_TIME}
               max={MAX_TIME.minusMinutes(TIME_STEP_IN_MINUTES)}
               stepInMinutes={TIME_STEP_IN_MINUTES}
               testId={'startTime'}
            />
         </StyledMaxWidth>
         <StyledMaxWidth maxWidth={150}>
            <TimePicker
               controller={props.register('endTime', {
                  required: true,
                  validate: async () => {
                     syncStartTime(props.setValue, props.getValue);
                     syncEstimatedDurationValue(props.setValue, props.getValue);

                     return props.trigger('startTime');
                  },
               })}
               label={appointmentsTranslationKeys.update.form.endTime}
               stepInMinutes={TIME_STEP_IN_MINUTES}
               min={MIN_TIME.plusMinutes(TIME_STEP_IN_MINUTES)}
               max={MAX_TIME}
               testId={'endTime'}
               roundMode={'ceil'}
            />
         </StyledMaxWidth>
      </>
   );
};
